import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  useFormContext,
  useWatch,
} from "react-hook-form";
import React, { useState, type FocusEvent } from "react";
import {
  SelectV2,
  type SelectV2Props,
} from "src/components/SelectV2/SelectV2.tsx";
import { composeRefs, useDebouncedValue } from "@exonn/hooks";
import type { SelectServerOptions } from "src/components/Form/Form.types.ts";
import { useInfiniteSelect } from "src/system/useInfiniteSelect.ts";
import { useSelect } from "src/system/useSelect.tsx";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { ResourceListModal } from "src/components/ResourceListModal/ResourceListModal.tsx";
import { useServerSelectFilter } from "src/system/useServerSelectFilter.ts";
import { useSelectOptions } from "src/system/useSelectOptions";
import { useSelectFieldOnChange } from "src/components/Form/fields/hooks/useSelectFieldOnChange.ts";
import { useSelectFieldFallbackFilter } from "src/components/Form/fields/hooks/useSelectFieldFallbackFilter.ts";
import type { GridRowParams } from "@mui/x-data-grid-premium";
import { getSelectValue } from "src/system/getSelectValue.ts";
import { trimInfiniteQuery, unique } from "@exonn/utils";

export type SelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
  forceValue?: any;
  locale?: string;
  serverOptions?: SelectServerOptions;
  getInitialOptionsFromMainResource?: boolean;
  onChange?: (value: string, resource: any, form: any) => void;
} & Omit<SelectV2Props, "onChange"> &
  Omit<ControllerProps<TFieldValues, TName>, "render">;

const DEFAULT_PAGE_SIZE = 15;

// todo: forward ref
export const SelectField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: SelectFieldProps<TFieldValues, TName>,
) => {
  const {
    name,
    disabled,
    options: optionsProp,
    onChange: onChangeProp,
    locale: lang,
    onBlur: onBlurProp,
    serverOptions,
    forceValue,
    isMultiple,
    getInitialOptionsFromMainResource = false,
    showAdvancedSearch,
    ...restComponentProps
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [isAdvancedSearchOpen, setAdvancedSearchOpen] = useState(false);

  const form = useFormContext();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [debouncedSearch] = useDebouncedValue(search, 500);

  const serverOptionsEnabled = !!serverOptions;

  const filter = useServerSelectFilter({
    ...serverOptions,
  });

  const value = useWatch({
    name: name,
  });

  const infiniteSelect = useInfiniteSelect({
    locale: lang,
    resource: serverOptions ? serverOptions.resource : "",
    search: debouncedSearch,
    include: serverOptions?.include,
    searchField: serverOptions?.searchField,
    sort: serverOptions?.sort,
    filter: filter,
    useSimplePagination: true,
    valueProp: serverOptions?.valueProp,
    labelProp: serverOptions?.labelProp,
    pageSize: DEFAULT_PAGE_SIZE,
    queryOptions: {
      enabled: serverOptionsEnabled && isOpen,
      refetchOnMount: false,
      gcTime: 0,
      staleTime: 0,
      placeholderData: keepPreviousData,
    },
  });

  const hasValue = isMultiple
    ? Array.isArray(value) && value.length > 0
    : value != null && value !== "";

  const fallbackFilter = useSelectFieldFallbackFilter({
    serverOptions,
    value,
  });

  const cachedResources = form.getValues(`${name}_resources`);

  const fallbackOptionsFromFormState = useSelectOptions({
    resources: cachedResources || [],
    valueProp: serverOptions?.valueProp,
    labelProp: serverOptions?.labelProp,
  });

  const fallbackOptionsSelect = useSelect({
    resource: serverOptions ? serverOptions.resource : "",
    valueProp: serverOptions?.valueProp,
    labelProp: serverOptions?.labelProp,
    locale: lang,
    include: serverOptions?.include,
    filter: fallbackFilter,
    queryOptions: {
      enabled:
        serverOptionsEnabled && hasValue && !getInitialOptionsFromMainResource,
      placeholderData: keepPreviousData,
    },
  });

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && serverOptions) {
      trimInfiniteQuery(queryClient, infiniteSelect.queryKey);
      infiniteSelect.query.refetch();

      if (hasValue && !getInitialOptionsFromMainResource) {
        fallbackOptionsSelect.query.refetch();
      }
    }
  };

  const handleScrollEnd = () => {
    if (!infiniteSelect.query.isFetchingNextPage) {
      infiniteSelect.query.fetchNextPage({
        cancelRefetch: false,
      });
    }
  };

  const handleChange = useSelectFieldOnChange({
    onChange: onChangeProp,
    name,
    isMultiple,
    getInitialOptionsFromMainResource,
    valueProp: serverOptions?.valueProp,
  });

  const options = serverOptions ? infiniteSelect.options : optionsProp;

  const isLoadingData = serverOptions
    ? infiniteSelect.query.isFetching &&
      !infiniteSelect.query.isFetchingNextPage
    : false;

  const handleBlur =
    ({ onBlur }: { onBlur: () => void }) =>
    (event: FocusEvent) => {
      onBlurProp?.(event);
      onBlur();
    };

  const fallbackOptions = [
    ...fallbackOptionsFromFormState,
    ...fallbackOptionsSelect.options,
  ];
  const isLoadingSelectedOption = getInitialOptionsFromMainResource
    ? false
    : fallbackOptionsSelect.query.isLoading;

  const isLoadingMoreOptions = infiniteSelect.query.isFetchingNextPage;

  const handleRowClick = (params: GridRowParams) => {
    const onChange = (value: any) => {
      form.setValue(name, value, {
        shouldDirty: true,
      });
    };
    const value = getSelectValue({
      item: params.row._resource,
      valueProp: serverOptions?.valueProp,
    });
    const nextValue = isMultiple
      ? unique([...(form.getValues(name) ?? []), value])
      : value;
    handleChange({
      onChange: onChange,
      resources: [params.row._resource],
    })(nextValue);
  };

  const handleSearchChange = (value: string) => {
    const queryKey = infiniteSelect.getQueryKey({
      search: value,
    });
    trimInfiniteQuery(queryClient, queryKey);
    setSearch(value);
  };

  return (
    <>
      <Controller
        key={name}
        name={name}
        control={form.control}
        render={({
          field: { name, value, onChange, onBlur, ref },
          formState: { errors },
        }) => {
          const resolvedValue = forceValue != null ? forceValue : value;
          return (
            <SelectV2
              value={resolvedValue}
              name={name}
              fallbackOptions={fallbackOptions}
              showSearch={true}
              isLoadingMoreOptions={isLoadingMoreOptions}
              onScrollEnd={handleScrollEnd}
              isLoadingSelectedOption={isLoadingSelectedOption}
              onOpenChange={handleOpenChange}
              isLoadingData={isLoadingData}
              disabled={disabled}
              isOpen={isOpen}
              isMultiple={isMultiple}
              onChange={handleChange({
                onChange,
                resources: infiniteSelect.resources,
              })}
              searchValue={search}
              onSearchValueChange={handleSearchChange}
              options={options}
              onBlur={handleBlur({
                onBlur,
              })}
              error={
                errors?.root?.[name]?.message // server error
              }
              showAdvancedSearch={showAdvancedSearch}
              onAdvancedSearchOpen={setAdvancedSearchOpen}
              {...restComponentProps}
              ref={composeRefs(ref)}
            />
          );
        }}
      />
      {serverOptions?.resource && (
        <ResourceListModal
          resource={serverOptions?.resource}
          withoutDescendants={serverOptions?.withoutDescendants}
          isModalOpen={isAdvancedSearchOpen}
          onModalOpen={setAdvancedSearchOpen}
          include={serverOptions?.include}
          onRowClick={handleRowClick}
        />
      )}
    </>
  );
};
